import VideoAttribution, { videoPath } from "@/components/VideoAttribution";
import { useAuth } from "@/context/AuthContext";
import LiveDemoBadge from "@/components/LiveDemoBadge";
import MediaImage from "@/components/MediaImage";
import { explicitDemoMedia, isDemoMedia } from "@/lib/media";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Eye, Zap, MessagesSquare, Gavel } from "lucide-react";
import { formatNumber, timeAgo, classNames } from "@/lib/format";
import PlatformBadge from "@/components/PlatformBadge";

export default function VideoCard({ video, compact = false, originAvailable = true }) {
  const { canEdit, user } = useAuth();
  // originAvailable=false: a reduced projection without url/source, so only an explicit Demo source is trusted.
  const demo = originAvailable ? isDemoMedia(video) : explicitDemoMedia(video);
  return (
    <div className="min-w-0">
    <Link
      to={videoPath(video)}
      data-testid={`video-card-${video.id}`}
      className="group block rounded-xl border border-border bg-card overflow-hidden hover:border-primary/60 transition-colors"
    >
      <div className="relative aspect-[9/12] bg-muted overflow-hidden">
        <MediaImage
          src={video.thumbnail}
          alt=""
          demo={demo}
          className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
        />
        {/* T1: one wrapping row. When the badges do not fit side by side (narrow cards, wider font metrics),
            the multiplier moves to a second line on the right instead of covering the platform or Demo badge. */}
        <div className="absolute inset-x-2 top-2 flex flex-wrap items-start justify-between gap-1">
          <div className="flex flex-wrap items-center gap-y-1">
            <PlatformBadge platform={video.platform} />
            <LiveDemoBadge video={video} originAvailable={originAvailable} />
          </div>
          {video.is_outlier && (
            <div
              data-testid="video-card-outlier-badge"
              className="outlier-glow ml-auto flex items-center gap-1 rounded-full bg-[hsl(var(--emerald))] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5"
            >
              <Zap className="h-3 w-3" />
              {video.outlier_multiplier.toFixed(1)}x
            </div>
          )}
        </div>
        {/* T1: one bottom row (notes · Decision · duration) that wraps upward instead of overlapping when the
            badges do not fit side by side (narrow cards, wider font metrics). The two side slots grow equally,
            so with room Decision stays exactly centred, notes left and duration right, as before. Without room,
            the duration slot wraps above, still on the right. DOM order is unchanged (it is part of the link's
            text); only the visual order uses `order`. */}
        <div className="absolute inset-x-2 bottom-2 flex flex-wrap-reverse items-end gap-1">
          <div className="order-3 flex min-w-max flex-1 justify-end">
            <div className="font-mono text-[10px] font-medium bg-black/70 text-white rounded px-1.5 py-0.5">
              {video.duration_seconds}s
            </div>
          </div>
          <div className="order-1 flex min-w-max flex-1">
            {video.comment_count > 0 && (
              <div
                data-testid="video-card-comment-count"
                title={`${video.comment_count} team note${video.comment_count === 1 ? "" : "s"}${video.unread_count > 0 ? ` · ${video.unread_count} unread` : ""}`}
                className="flex items-center gap-1 rounded-full bg-primary text-primary-foreground font-mono text-[10px] font-semibold px-1.5 py-0.5"
              >
                <MessagesSquare className="h-3 w-3" />{video.comment_count}
                {video.unread_count > 0 && <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--rose))] ring-2 ring-primary" data-testid="video-card-unread-dot" />}
              </div>
            )}
          </div>
          {video.decision_count > 0 && (
            <div data-testid="video-card-decision-badge" title="Has a team decision" className="order-2 flex items-center gap-1 rounded-full bg-[hsl(var(--amber))] text-white text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5">
              <Gavel className="h-2.5 w-2.5" /> Decision
            </div>
          )}
        </div>
      </div>
      <div className={classNames("p-3 space-y-2", compact && "p-2.5")}>
        <div className="op-card-title line-clamp-2 min-h-[2.5rem]">
          {video.title}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <MediaImage src={video.creator_avatar} alt="" name={video.creator_handle} kind="avatar" demo={demo} className="h-5 w-5 rounded-full object-cover" />
          <span className="truncate">{video.creator_handle}</span>
          <span className="ml-auto shrink-0">{timeAgo(video.published_at)}</span>
        </div>
        <VideoAttribution video={video} size="card" />
        <div className="flex items-center gap-3 text-xs font-mono text-muted-foreground pt-1 border-t border-border">
          <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{formatNumber(video.views)}</span>
          <span className="flex items-center gap-1"><Heart className="h-3 w-3" />{formatNumber(video.likes)}</span>
          <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3" />{formatNumber(video.comments)}</span>
          <span className="ml-auto text-foreground font-semibold">{video.engagement_rate}%</span>
        </div>
      </div>
    </Link>
    {canEdit && user?.workspace_id && <Link to={videoPath(video, `/videos/${video.id}?tab=thread`)} data-testid={`video-card-decision-note-${video.id}`} aria-label="Ajouter une note de décision" title="Ajouter une note de décision" className="mt-1 inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-primary focus-visible:ring-2 focus-visible:ring-ring"><Gavel className="h-3.5 w-3.5" aria-hidden="true" />Ajouter une note de décision</Link>}
    </div>
  );
}
